import React, { useMemo } from 'react'
import {
    Box,
    TextProps,
    BoxProps,
    Menu,
    Button,
    Flex,
    MenuButton,
    MenuItem,
    MenuList,
    useDisclosure,
    MenuListProps,
    Input,
    ButtonProps,
    MenuGroup,
    CloseButton,
    HStack,
} from '@chakra-ui/react'
import SharedLabel from './shared-label'
import { ChevronDownIcon, CheckIcon } from '@chakra-ui/icons'
import { useDebounce } from '~utils/hooks/use-debounce'
import { adjustArrayWithItem } from '~utils/helpers'

interface Props<T> extends Omit<ButtonProps, 'value' | 'onChange'> {
    label?: string
    labelProps?: TextProps
    containerProps?: BoxProps
    displayTransformer?: (o: T) => string
    secondaryDisplayTransformer?: (o: T) => string
    searchByFields?: (keyof T)[]
    options: T[]
    selectedValues: T[]
    setSelectedValues: (selectedValues: T[]) => void
    /** Spread onto the dropdown's `MenuList` (e.g. `zIndex`, `rounded`). */
    menuProps?: MenuListProps
    listItem?: (item: T) => React.ReactNode
    clickToCancel?: boolean
    onCancel?: () => void
    placeholder?: string
}

const GenericMultiselect = <T extends any>({
    label,
    labelProps,
    containerProps,
    displayTransformer = (o: T) => String(o),
    secondaryDisplayTransformer,
    searchByFields,
    options,
    selectedValues,
    setSelectedValues,
    menuProps,
    listItem,
    clickToCancel,
    onCancel,
    placeholder,
    ...props
}: Props<T>) => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    const [search, setSearch] = React.useState('')
    const debouncedSearch = useDebounce(search, 1000)
    const filtered = useMemo(() => {
        if (!searchByFields) {
            return options
        }

        return options.filter(o => {
            return searchByFields.some(field => {
                const val = o[field]
                if (typeof val === 'string') {
                    return val.toLowerCase().includes(search.toLowerCase())
                }
                return false
            })
        })
    }, [debouncedSearch, options])

    const displayText = selectedValues.length
        ? `${
              displayTransformer
                  ? displayTransformer(selectedValues[0])
                  : selectedValues[0]
          } ${selectedValues.length > 1 ? `+${selectedValues.length - 1}` : ''}`
        : placeholder || 'Select'

    return (
        <Box {...containerProps}>
            {label && <SharedLabel {...labelProps}>{label}</SharedLabel>}
            <Menu isOpen={isOpen} onClose={onClose} closeOnSelect={false}>
                <HStack>
                    <MenuButton
                        as={Button}
                        variant="outline"
                        rightIcon={<ChevronDownIcon />}
                        textAlign="left"
                        onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            onOpen()
                        }}
                        {...props}
                    >
                        {displayText}
                    </MenuButton>
                    {clickToCancel && !!selectedValues.length && (
                        <CloseButton
                            size={props?.size || 'md'}
                            onClick={() => {
                                onCancel?.()
                                setSelectedValues([])
                            }}
                        />
                    )}
                </HStack>
                <MenuList
                    maxH={'296px'}
                    maxW="380px"
                    minW="280px"
                    overflowY="auto"
                    {...menuProps}
                >
                    {searchByFields && (
                        <Box px={2} mb={2}>
                            <Input
                                rounded="lg"
                                size="sm"
                                placeholder="Search..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onBlur={e => {
                                    e.target.focus()
                                }}
                            />
                        </Box>
                    )}
                    {filtered &&
                        filtered.map((o, index) => {
                            const curVal = o
                            return (
                                <MenuItem
                                    key={`${displayTransformer(o)}-${index}`}
                                    icon={
                                        selectedValues.includes(curVal) ? (
                                            <CheckIcon />
                                        ) : (
                                            <Box w="12px" />
                                        )
                                    }
                                    onClick={e => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        setSelectedValues(
                                            adjustArrayWithItem(
                                                selectedValues,
                                                curVal,
                                            ),
                                        )
                                    }}
                                >
                                    <Flex align="center">
                                        {listItem ? (
                                            listItem(o)
                                        ) : (
                                            <>{displayTransformer(o)}</>
                                        )}
                                    </Flex>
                                </MenuItem>
                            )
                        })}
                </MenuList>
            </Menu>
        </Box>
    )
}

export default GenericMultiselect
